import React, { useCallback, useEffect, useRef, useState } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

export default function FilmStrip({
  rangeStart,
  rangeEnd,
  totalSlides,
  previousBrowsePage,
  nextBrowsePage,
  visibleLooks = [],
  activeIndex,
  selectLook,
  section = {},
  currentPage = 1,
  totalPages = 1,
}) {
  const filmstripRef = useRef(null);
  const [scrollState, setScrollState] = useState({
    canScrollLeft: false,
    canScrollRight: false,
  });

  const computedTotalPages =
    totalPages > 1
      ? totalPages
      : totalSlides > visibleLooks.length && visibleLooks.length > 0
        ? Math.ceil(totalSlides / visibleLooks.length)
        : 1;
  const computedCurrentPage = currentPage || 1;
  const hasMultiplePages = computedTotalPages > 1;

  const hasPrevPage = computedCurrentPage > 1 || rangeStart > 1;
  const hasNextPage = computedCurrentPage < computedTotalPages || rangeEnd < totalSlides;

  const checkScroll = useCallback(() => {
    const el = filmstripRef.current;
    if (!el) return;
    const canScrollLeft = el.scrollLeft > 6;
    const canScrollRight = el.scrollLeft < el.scrollWidth - el.clientWidth - 6;
    setScrollState((prev) => {
      if (
        prev.canScrollLeft === canScrollLeft &&
        prev.canScrollRight === canScrollRight
      ) {
        return prev;
      }
      return { canScrollLeft, canScrollRight };
    });
  }, []);

  useEffect(() => {
    // Check initial scroll state
    checkScroll();
    const el = filmstripRef.current;
    if (!el) return;

    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [checkScroll, visibleLooks]);

  // Keep active thumbnail scrolled into view on mobile
  useEffect(() => {
    const el = filmstripRef.current;
    if (!el || activeIndex === undefined || activeIndex === null) return;
    const activeItem = el.children[activeIndex];
    if (activeItem && typeof activeItem.scrollIntoView === "function") {
      activeItem.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [activeIndex]);

  const handlePrev = (e) => {
    e?.preventDefault?.();
    const el = filmstripRef.current;
    if (el && el.scrollLeft > 8) {
      el.scrollBy({
        left: -Math.max(el.clientWidth * 0.75, 180),
        behavior: "smooth",
      });
    } else if (hasPrevPage) {
      previousBrowsePage?.();
    }
  };

  const handleNext = (e) => {
    e?.preventDefault?.();
    const el = filmstripRef.current;
    if (el && el.scrollLeft < el.scrollWidth - el.clientWidth - 8) {
      el.scrollBy({
        left: Math.max(el.clientWidth * 0.75, 180),
        behavior: "smooth",
      });
    } else if (hasNextPage) {
      nextBrowsePage?.();
    }
  };

  if (!visibleLooks || visibleLooks.length === 0 || totalSlides === 0) {
    return null;
  }

  const isPrevDisabled = !scrollState.canScrollLeft && !hasPrevPage;
  const isNextDisabled = !scrollState.canScrollRight && !hasNextPage;

  return (
    <div className="app-footer-bar h-[140px] lg:h-[150px] py-1.5 lg:py-2 flex flex-col justify-center overflow-hidden shrink-0">
      <div className="w-full px-3 lg:px-4 flex flex-col justify-between h-full min-h-0">
        <div className="flex justify-between items-center mb-0 w-full flex-shrink-0">
          <h3 className="font-headline-md text-[10px] lg:text-[12px] leading-4 font-bold text-primary uppercase tracking-wider">
            Browse transformations
          </h3>
          {hasMultiplePages && (
            <div className="flex items-center gap-2 lg:gap-4">
              <span
                id="range-label"
                className="text-[10px] lg:text-xs text-secondary font-semibold"
              >
                Page {computedCurrentPage} of {computedTotalPages}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handlePrev}
                  disabled={isPrevDisabled}
                  className={`app-icon-button w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                    isPrevDisabled
                      ? "opacity-35 cursor-not-allowed pointer-events-none text-slate-300 border-slate-200"
                      : "cursor-pointer hover:scale-105 active:scale-95 text-slate-600 hover:text-slate-900"
                  }`}
                  type="button"
                  aria-label="Previous page"
                >
                  <MdChevronLeft size={18} />
                </button>
                <button
                  onClick={handleNext}
                  disabled={isNextDisabled}
                  className={`app-icon-button w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                    isNextDisabled
                      ? "opacity-35 cursor-not-allowed pointer-events-none text-slate-300 border-slate-200"
                      : "cursor-pointer hover:scale-105 active:scale-95 text-slate-600 hover:text-slate-900"
                  }`}
                  type="button"
                  aria-label="Next page"
                >
                  <MdChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>
        <div
          ref={filmstripRef}
          onScroll={checkScroll}
          id="filmstrip"
          className="flex lg:grid lg:grid-cols-10 gap-2 lg:gap-3 w-full flex-1 min-h-0 items-center justify-start lg:justify-center overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 scroll-smooth"
        >
          {visibleLooks.map((look, pageIndex) => {
            const index = rangeStart + pageIndex - 1;
            const originalImage = look.original || look.slides?.[0]?.original || "";
            const transformedPreviewImage =
              look.slides?.[0]?.preview ||
              look.slides?.[0]?.transformed ||
              look.preview ||
              look.transformed ||
              "";
            const titleText =
              look.productTitle ||
              look.productName ||
              look.title ||
              look.originalAlt ||
              section.title ||
              "Product";
            const generationsCount = look.slides?.length || look.count || 0;

            return (
              <button
                key={look.iProductId || index}
                onClick={() => selectLook(section, pageIndex)}
                className="relative w-[94px] lg:w-full lg:max-w-[96px] min-w-[94px] lg:min-w-0 h-[88px] lg:h-[98px] mx-auto flex flex-col group cursor-pointer border-none bg-transparent shrink-0"
                type="button"
                title={titleText}
              >
                <div
                  className={`w-full h-[88px] lg:h-[98px] box-border rounded-lg ${
                    pageIndex === activeIndex
                      ? "border-2 border-primary"
                      : "border border-outline"
                  } flex overflow-hidden shadow-sm transition-transform group-hover:scale-[1.02] relative`}
                >
                  <div className="w-1/2 h-full bg-surface-container">
                    <img
                      className="w-full h-full object-contain lg:object-cover"
                      src={originalImage}
                      alt={`Original ${titleText}`}
                    />
                  </div>
                  <div className="w-1/2 h-full bg-surface border-l border-outline relative">
                    <img
                      className="w-full h-full object-contain lg:object-cover"
                      src={transformedPreviewImage}
                      alt={`Generated ${titleText}`}
                    />
                    {generationsCount > 1 && (
                      <span className="absolute bottom-1 right-1 bg-slate-950/70 backdrop-blur-xs text-white text-[8px] font-bold px-1 py-0.5 rounded leading-none">
                        +{generationsCount}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
